import {
  CUBE_API,
  CUBE_KEY,
  DEFAULT_CUBE_PAGE_LIMIT,
  GroupOrColDef,
  OPERATOR_MAP,
} from "./constants"
import {
  IServerSideDatasource,
  IServerSideGetRowsParams,
  IServerSideGetRowsRequest,
} from "ag-grid-community"
import { ReportPayload, getFieldMeta } from "./utils"
import cubejs, {
  BinaryOperator,
  Filter,
  PivotConfig,
  Query,
} from "@cubejs-client/core"

import { Cube } from "@cubejs-client/core"
import { debounce } from "lodash/fp"
import { getToken } from "providers/A12n"
import { httpExport } from "api/http"
import { numberFormatter } from "./utils"
import { zonedTimeToUtc } from "date-fns-tz"

export const getCubeApi = async () => {
  const at = await getToken()
  if (!CUBE_API || !CUBE_KEY || !at) return undefined
  return cubejs(CUBE_KEY, {
    apiUrl: CUBE_API,
    headers: {
      Authorization: `Bearer ${at}`,
    },
  })
}

export async function fetchCubeResults(
  query: Query,
  pivotConfig?: PivotConfig,
  hasToRemoveFirstRow?: boolean,
): Promise<undefined | Array<Record<string, any>>> {
  const cubeJsApi = await getCubeApi()
  const res = await cubeJsApi?.load(query)

  if (!!pivotConfig) {
    return res?.tablePivot(pivotConfig)
  }

  // We do that for (FD - Druid) because after Cube version 34.2, we get the header as a first row in response.
  // MR: https://github.com/cube-js/cube/pull/7414/files
  if (hasToRemoveFirstRow) {
    return res?.rawData().slice(1)
  }

  // We do that in order to handle null values in not pivot mode, since tablePivot converts null into zero
  return res?.rawData()
}

/*
  we prepare a pivot config, based on cubejs way of pivoting `{x, y}`
  x holds all dimensions and y holds measures and select pivot values
*/
function preparePivotConfig(query: Query, req: IServerSideGetRowsRequest) {
  const pivotIds = req.pivotCols?.map((pc) => pc.id)
  const x = [...(query.dimensions?.filter((d) => !pivotIds.includes(d)) || [])]
  const y = [...(query.measures || []), ...(pivotIds || [])]
  return { x, y }
}

export function dataSource(
  query: Query,
  timeZone: string,
  setParams: (value: IServerSideGetRowsParams<any>) => void,
  meta?: Cube[],
  hasToRemoveFirstRow?: boolean,
): IServerSideDatasource {
  return {
    getRows: debounce(500, async (params) => {
      if (!CUBE_API) return params.fail()
      setParams(params)
      try {
        // pivot enabled
        if (params.request.pivotMode) {
          setPivotColumns(params, timeZone, meta)
          const tquery = transformPivotQuery(params, timeZone)

          if (!!tquery) {
            const pivotConfig = preparePivotConfig(tquery, params.request)
            const rowData = await fetchCubeResults(tquery, pivotConfig)
            if (rowData) {
              params.success({ rowData })
            }
          } else {
            params.success({ rowData: [] })
          }
          // simple grid
        } else {
          const tquery = transformQuery(query, params, timeZone)
          const rowData = await fetchCubeResults(
            tquery,
            undefined,
            hasToRemoveFirstRow,
          )
          if (rowData) params.success({ rowData })
        }
      } catch {
        params.fail()
      }
    }),
  }
}

//

/*
  we apply limit & offset for paginated results. Currently the
  grid works with the infinite scrolling model
*/
function withPagination(query: Query, req: IServerSideGetRowsRequest) {
  const { startRow, endRow } = req
  const offset = startRow ?? 0
  const limit = !!endRow ? endRow - offset : DEFAULT_CUBE_PAGE_LIMIT

  return { ...query, limit, offset }
}

// we apply the selected order to the passed query
function withOrder(query: Query, req: IServerSideGetRowsRequest): Query {
  const sm = req.sortModel
  if (sm?.length) {
    const order = sm.map((s) => [s.colId, s.sort] as [string, "asc" | "desc"])
    return { ...query, order }
  }

  return query
}
/*
  for all grid filters, set by the user, we create and push the
  appropriate query filters to the incoming query
*/
function withFilters(
  query: Query,
  req: IServerSideGetRowsRequest,
  timeZone: string,
) {
  const fm = req.filterModel

  if (Object.keys(fm)?.length) {
    const filters: Filter[] = !!query.filters ? [...query.filters] : []

    const perType = Object.keys(fm).reduce(
      (agg, fkey) => {
        const { filterType, ...rest } = fm[fkey]

        return {
          ...agg,
          [filterType]: { ...(agg[filterType] || {}), [fkey]: { ...rest } },
        }
      },
      {} as Record<string, Record<string, any>>,
    )

    // for set type filters
    const setFilters = perType["set"] || {}
    Object.entries(setFilters).forEach(([key, props]) => {
      const setValues = props.values?.filter(Boolean)
      const notSet = !!props.values?.filter((v?: string) => v === null).length

      if (!!setValues?.length && notSet) {
        filters.push({
          or: [
            {
              member: key,
              operator: "equals",
              values: setValues,
            },
            {
              member: key,
              operator: "notSet",
            },
          ],
        })
      }

      if (!!setValues?.length && !notSet) {
        filters.push({
          member: key,
          operator: "equals",
          values: setValues,
        })
      }

      if (!setValues?.length && notSet) {
        filters.push({
          member: key,
          operator: "notSet",
        })
      }
    })

    // for date type filters
    const dateFilters = perType["date"] || {}
    Object.entries(dateFilters).forEach(([key, props]) => {
      const dateType = props.type
      if (!!dateType) {
        const operators = {
          ...OPERATOR_MAP["default"],
          ...OPERATOR_MAP["date"],
        }

        const operator = operators[
          props.type as keyof typeof operators
        ] as BinaryOperator

        const dateFrom =
          props.dateFrom && zonedTimeToUtc(new Date(props.dateFrom), timeZone)
        const dateTo =
          props.dateTo && zonedTimeToUtc(new Date(props.dateTo), timeZone)

        if (dateType === "inRange" && !!dateFrom && !!dateTo) {
          filters.push({
            member: key,
            operator,
            values: [dateFrom, dateTo],
          })
        } else {
          filters.push({
            member: key,
            operator,
            values: [dateFrom],
          })
        }
      }
    })
    // for number type filters
    const numberFilters = perType["number"] || {}

    Object.entries(numberFilters).forEach(([key, props]) => {
      const operators = {
        ...OPERATOR_MAP["default"],
      }
      const numberType = props.type
      if (numberType === "inRange") {
        filters.push({
          and: [
            {
              member: key,
              operator: "gte",
              values: [props.filter.toString()],
            },
            {
              member: key,
              operator: "lte",
              values: [props.filterTo.toString()],
            },
          ],
        })
      } else {
        const operator = operators[
          props.type as keyof typeof operators
        ] as BinaryOperator
        filters.push({
          member: key,
          operator,
          values: [props.filter?.toString()],
        })
      }
    })

    return { ...query, filters }
  }

  return query
}

/*
  we get the group collection from the grid and apply the neccessary
  filters so that on each drilling down (for example Campaign -> Media Type)
  we get the respective, grouped values
*/
function withGrouping(query: Query, req: IServerSideGetRowsRequest) {
  let updatedQuery = { ...query, filters: query.filters || [] }
  const { rowGroupCols, groupKeys } = req

  // grouping
  if (!!rowGroupCols?.length) {
    const groupIds = rowGroupCols.map((gc) => gc.id)
    const applicableGroupIds = groupIds.slice(0, (groupKeys.length ?? 0) + 1)
    updatedQuery.dimensions = query.dimensions?.filter((d) =>
      applicableGroupIds.includes(d),
    )
  }

  // drilling
  if (!!groupKeys.length && !!rowGroupCols.length) {
    if (groupKeys.length === rowGroupCols.length) {
      updatedQuery.dimensions = query.dimensions
    }

    rowGroupCols.forEach((gc, idx) => {
      !!groupKeys[idx] &&
        updatedQuery.filters.push({
          member: gc.id,
          operator: "equals",
          values: [groupKeys[idx].toString()],
        })
    })
  }

  return updatedQuery
}

/*
  We apply grouping, filtering, ordering and pagination to the provided
  query, based on the users's respective selections in the grid. This is
  the default query transformation for all queries, pivoted or not.
*/
export function transformQuery(
  query: Query,
  params: IServerSideGetRowsParams,
  timeZone: string,
) {
  const grouped = withGrouping(query, params.request)
  const filtered = withFilters(grouped, params.request, timeZone)
  const ordered = withOrder(filtered, params.request)
  if (params.columnApi.isPivotMode()) return ordered
  const paginated = withPagination(ordered, params.request)
  // const timezoned = withTimezone(paginated)

  return paginated
}

// const withTimezone = (query: Query) => {
//   const timezone = "UTC+8" //Intl.DateTimeFormat().resolvedOptions().timeZone
//   return { ...query, timezone }
// }

/*
  we read the grid's pivot and value column collections and
  call the createPivotColumns fn to produce the pivot value columns.
  We then set those on the grid by using the grid's API.
*/
async function setPivotColumns(
  params: IServerSideGetRowsParams,
  timeZone: string,
  meta?: Cube[],
) {
  const pivotColumnIds = params.request.pivotCols?.map((c) => c.id)
  const valueFieldIds = params.request.valueCols?.map((vc) => vc.id)

  if (!pivotColumnIds?.length || !valueFieldIds?.length) {
    params.columnApi.setPivotResultColumns([])
    return
  }

  const pivotColumns: Array<GroupOrColDef> = []
  await createPivotColumns(
    pivotColumns,
    pivotColumnIds,
    valueFieldIds,
    params.request,
    timeZone,
    meta,
  )

  if (!!pivotColumns.length)
    params.columnApi.setPivotResultColumns(pivotColumns)
}

/*
  it creates the pivot value columns based on each pivot column id
  (from the grid's pivot columns collection) the grid's
  values collection (valueFieldIds) and the passed parent column
*/
async function addPivotColumns(
  columnId: string,
  request: IServerSideGetRowsRequest,
  timeZone: string,
  valueFieldIds?: string[],
  parent?: GroupOrColDef,
  meta?: Cube[],
) {
  const columns: Array<GroupOrColDef> = []
  const _query: Query = { dimensions: [columnId] }
  //filtering for pivot columns that have been filtered
  const query = withFilters(_query, request, timeZone)
  const { filters = [] } = query

  query.dimensions = [...(query.dimensions || [])]

  const parentId = parent?.groupId
  const [parentValue] = parent?.pivotKeys?.slice(-1) || []

  /*
    we need to apply a filter to get the pivot values filtered by
    the parent value. So, i.e. if the pivot column is Spot Month,
    and the parent is Spot Quarter, we need to get the months for the
    parent quarter only
  */
  const isLeafNode = !!valueFieldIds
  if (parentId && !!parentValue) {
    filters?.push({
      member: parentId,
      operator: "equals",
      values: [parentValue.toString()],
    })
  }

  // we fetch the values that will produce the pivot columns
  const data = isLeafNode
    ? [{ [columnId]: parent?.pivotKeys?.slice(-1) }]
    : await fetchCubeResults({ ...query, filters })

  if (!!data?.length) {
    data.forEach((d) => {
      // we check if its a valueField column creation or not
      if (!valueFieldIds) {
        let field = `${d[columnId]}`
        /*
          we apply a comma-separated pattern for the fields,
          i.e for a column for spot quarter 1, spot month 2
          and day of week 20, looking for gross cost the field
          will be 1,2,20,Spot.grossCost
        */
        if (!!parent?.pivotKeys?.length) {
          field = `${parent.pivotKeys.join(",")},${field}`
        }

        const displayColumn =
          getFieldMeta(columnId, meta)?.shortTitle || columnId

        const column: GroupOrColDef = {
          field,
          groupId: columnId,
          sortable: false,
          headerName: `${displayColumn}: ${d[columnId]}`,
          pivotKeys: [...(parent?.pivotKeys || []), d[columnId]?.toString()],
          valueFormatter: numberFormatter,
        }
        columns.push(column)
      } else {
        const pivotColumns = valueFieldIds.map((value) => {
          const displayColumn = getFieldMeta(value, meta)?.shortTitle
          const field = `${parent?.pivotKeys?.join(",")},${value}`
          const pivotColumn: GroupOrColDef = {
            field,
            groupId: columnId,
            sortable: false,
            headerName: `${displayColumn}`,
            pivotKeys: [...(parent?.pivotKeys || []), field],
            valueFormatter: numberFormatter,
          }

          return pivotColumn
        })
        columns.push(...pivotColumns)
      }
    })
  }
  return columns
}
/*
  creates the pivot columns based on the grid's row group (columnIds)
  and value (valueFieldIds) collections
*/
async function createPivotColumns(
  pivotColumns: Array<GroupOrColDef>,
  columnIds: string[],
  valueFieldIds: string[],
  request: IServerSideGetRowsRequest,
  timeZone: string,
  meta?: Cube[],
) {
  let lastRoot: Array<GroupOrColDef> = []
  for (let i = 0; i < columnIds.length; i++) {
    const isRoot = i === 0
    const isLeaf = i === columnIds.length - 1
    const columnId = columnIds[i]
    // for the first pivot key, the value columns are the root
    if (isRoot) {
      const columns = await addPivotColumns(
        columnId,
        request,
        timeZone,
        undefined,
        undefined,
        meta,
      )
      pivotColumns.push(...(columns || []))
      lastRoot = pivotColumns
    }
    // all child nodes that are not leafs, are created here
    else if (!isLeaf) {
      const children = await Promise.all(
        lastRoot?.map(async (parent) => {
          const children = await addPivotColumns(
            columnId,
            request,
            timeZone,
            undefined,
            parent,
            meta,
          )
          parent.children = [...(parent.children || []), ...children]
          return parent.children
        }),
      )
      // keep the latest children, flat, as the latest node to append next
      lastRoot = children.flat()
    }
    // leaf nodes has special treatment as bellow
    if (isLeaf) {
      // if leaf node is not also a root node, we need to create the parent nodes
      if (!isRoot) {
        const children = await Promise.all(
          lastRoot?.map(async (parent) => {
            const children = await addPivotColumns(
              columnId,
              request,
              timeZone,
              undefined,
              parent,
              meta,
            )
            parent.children = [...(parent.children || []), ...children]
            return parent.children
          }),
        )
        lastRoot = children.flat()
      }

      await Promise.all(
        lastRoot?.map(async (parent) => {
          const children = await addPivotColumns(
            columnId,
            request,
            timeZone,
            valueFieldIds,
            parent,
            meta,
          )
          parent.children = [...(parent.children || []), ...children]
          return parent.children
        }),
      )
    }
  }
}

export function transformPivotQuery(
  params: IServerSideGetRowsParams,
  timeZone: string,
) {
  // dimensions from groups
  const dimensions = params.request.rowGroupCols?.map((gc) => gc.id)
  if (!dimensions?.length) return undefined

  // measures from values
  const measures = params.request.valueCols?.map((vc) => vc.id)

  // query
  const query = {
    measures,
    dimensions,
  }

  // apply default transformation
  const transformed = transformQuery(query, params, timeZone)

  // append pivoting dimensions
  const pivotDimensions = params.request.pivotCols?.map((c) => c.id)
  transformed.dimensions = [
    ...(transformed.dimensions || []),
    ...(pivotDimensions || []),
  ]

  return transformed
}

export const generateReport = async (payload: ReportPayload) => {
  const res = await httpExport.post(`/export/report-builder-export`, {
    json: payload,
    prefixUrl: "",
    timeout: false,
  })
  return {
    blob: await res.blob(),
    filename: res.headers.get("content-disposition")!.split("filename=")[1],
  }
}
